/**
 * Handles the saturation and brightness area mouse down event.
 *
 * @param {MouseEvent} e - The mouse event.
 */
export function handleSBMouseDown(e) {
  e.preventDefault()

  const handleMouseMove = (e) => this.call('updateSBFromEvent', e)
  const handleMouseUp = () => {
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)
  }

  document.addEventListener('mousemove', handleMouseMove)
  document.addEventListener('mouseup', handleMouseUp)
}

/**
 * Handles the hue area mouse down event.
 *
 * @param {MouseEvent} e - The mouse event.
 */
export function handleHueMouseDown(e) {
  e.preventDefault()

  const handleMouseMove = (e) => this.call('updateHueFromEvent', e)
  const handleMouseUp = () => {
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)
  }

  document.addEventListener('mousemove', handleMouseMove)
  document.addEventListener('mouseup', handleMouseUp)
}

/**
 * Updates the saturation and brightness area from a mouse event.
 *
 * @param {MouseEvent} e - The mouse event.
 */
export function updateSBFromEvent(e) {
  const rect = this.node.getBoundingClientRect()
  const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left))
  const y = Math.max(0, Math.min(rect.height, e.clientY - rect.top))

  const saturation = x / rect.width
  const brightness = 1 - y / rect.height

  this.state.update({
    saturation,
    brightness
  })
}

/**
 * Updates the hue value from a mouse event.
 *
 * @param {MouseEvent} e - The mouse event.
 */
export function updateHueFromEvent(e) {
  const rect = this.node.getBoundingClientRect()
  const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left))

  const hue = (x / rect.width) * 360

  this.state.update({
    hue
  })
}

/**
 * Converts HSB (Hue, Saturation, Brightness) color values to RGB (Red, Green, Blue).
 *
 * @param {number} h - Hue value (0-360)
 * @param {number} s - Saturation value (0-1)
 * @param {number} b - Brightness value (0-1)
 * @returns {{r: number, g: number, b: number}} Object containing r, g, b values (0-255)
 */
export function hsbToRgb(h, s, b) {
  h = h / 360
  const i = Math.floor(h * 6)
  const f = h * 6 - i
  const p = b * (1 - s)
  const q = b * (1 - f * s)
  const t = b * (1 - (1 - f) * s)

  let r, g, blue
  switch (i % 6) {
    case 0:
      r = b
      g = t
      blue = p
      break
    case 1:
      r = q
      g = b
      blue = p
      break
    case 2:
      r = p
      g = b
      blue = t
      break
    case 3:
      r = p
      g = q
      blue = b
      break
    case 4:
      r = t
      g = p
      blue = b
      break
    case 5:
      r = b
      g = p
      blue = q
      break
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(blue * 255)
  }
}

/**
 * Converts RGB (Red, Green, Blue) color values to HSB (Hue, Saturation, Brightness).
 *
 * @param {number} r - Red value (0-255)
 * @param {number} g - Green value (0-255)
 * @param {number} b - Blue value (0-255)
 * @returns {{h: number, s: number, b: number}} Object containing h, s, b values (h: 0-360, s,b: 0-1)
 */
export function rgbToHsb(r, g, b) {
  r /= 255
  g /= 255
  b /= 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const diff = max - min

  let h = 0
  let s = max === 0 ? 0 : diff / max
  let brightness = max

  if (diff !== 0) {
    switch (max) {
      case r:
        h = ((g - b) / diff + (g < b ? 6 : 0)) / 6
        break
      case g:
        h = ((b - r) / diff + 2) / 6
        break
      case b:
        h = ((r - g) / diff + 4) / 6
        break
    }
  }

  return {
    h: h * 360,
    s: s,
    b: brightness
  }
}

/**
 * Converts RGB (Red, Green, Blue) color values to a hex color string.
 *
 * @param {number} r - Red value (0-255)
 * @param {number} g - Green value (0-255)
 * @param {number} b - Blue value (0-255)
 * @returns {string} Hex color string (e.g., "#FF0000" for red)
 */
export function rgbToHex(r, g, b) {
  return (
    '#' +
    ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()
  )
}

/**
 * Converts a hex color string to RGB (Red, Green, Blue) values.
 *
 * @param {string} hex - Hex color string (e.g., "#FF0000" or "#F00")
 * @returns {{r: number, g: number, b: number}|null} Object containing r, g, b values (0-255) or null if invalid
 */
export function hexToRgb(hex) {
  // Remove # if present
  hex = hex.replace('#', '')

  // Handle 3-digit hex (expand to 6-digit)
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((char) => char + char)
      .join('')
  }

  // Handle 6-digit hex
  if (hex.length === 6) {
    const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16)
        }
      : null
  }

  return null
}

/**
 * Converts a hex color string to HSB (Hue, Saturation, Brightness) values.
 *
 * @param {string} hex - Hex color string (e.g., "#FF0000" or "#F00")
 * @returns {{h: number, s: number, b: number}|null} Object containing h, s, b values (h: 0-360, s,b: 0-1) or null if invalid
 */
export function hexToHsb(hex) {
  // First convert hex to RGB
  const rgb = hexToRgb(hex)
  if (!rgb) return null

  // Then convert RGB to HSB
  return rgbToHsb(rgb.r, rgb.g, rgb.b)
}

/**
 * Converts HSB (Hue, Saturation, Brightness) color values to a hex color string.
 *
 * @param {number} h - Hue value (0-360)
 * @param {number} s - Saturation value (0-1)
 * @param {number} b - Brightness value (0-1)
 * @returns {string|null} Hex color string (e.g., "#FF0000" for red) or null if invalid
 */
export function hsbToHex(h, s, b) {
  // First convert HSB to RGB
  const rgb = hsbToRgb(h, s, b)
  if (!rgb) return null

  // Then convert RGB to hex
  return rgbToHex(rgb.r, rgb.g, rgb.b)
}

/**
 * Converts HSB (Hue, Saturation, Brightness) color values to HSL (Hue, Saturation, Lightness) values.
 *
 * @param {number} h - Hue value (0-360)
 * @param {number} s - Saturation value (0-1)
 * @param {number} b - Brightness value (0-1)
 * @returns {{h: number, s: number, l: number}} Object containing h (0-360), s (0-100), l (0-100) values
 */
export function hsbToHsl(h, s, b) {
  const l = (b * (2 - s)) / 2
  const sl = l !== 0 && l !== 1 ? (b - l) / Math.min(l, 1 - l) : 0

  return {
    h: h,
    s: sl * 100,
    l: l * 100
  }
}

/**
 * Converts HSL (Hue, Saturation, Lightness) color values to HSB (Hue, Saturation, Brightness) values.
 *
 * @param {number} h - Hue value (0-360)
 * @param {number} s - Saturation value (0-100)
 * @param {number} l - Lightness value (0-100)
 * @returns {{h: number, s: number, b: number}} Object containing h (0-360), s (0-1), b (0-1) values
 */
export function hslToHsb(h, s, l) {
  // Convert percentage values to decimals (0-1)
  const sDecimal = s / 100
  const lDecimal = l / 100

  const b = lDecimal + sDecimal * Math.min(lDecimal, 1 - lDecimal)
  const sb = b === 0 ? 0 : 2 * (1 - lDecimal / b)

  return {
    h: h,
    s: sb,
    b: b
  }
}

/**
 * Checks if a string is a valid hex color code.
 *
 * @param {string} hex - The string to check
 * @returns {boolean} True if the string is a valid hex color code
 * @private
 */
function isValidHex(hex) {
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex)
}

/**
 * Checks if the given value is a valid RGB number.
 *
 * @param value
 * @returns {boolean}
 */
/**
 * Checks if a value is a valid RGB number (0-255).
 *
 * @param {string|number} value - The value to check
 * @returns {boolean} True if the value is a valid RGB number
 * @private
 */
function isValidRgbNumber(value) {
  // Check if it's a valid number string
  if (typeof value !== 'string' && typeof value !== 'number') {
    return false
  }

  const num = Number(value)

  // Check if it's a valid number, integer, and within RGB range
  return !isNaN(num) && Number.isInteger(num) && num >= 0 && num <= 255
}

/**
 * Checks if the given value is a valid color.
 *
 * @param {string|number} value - The value to check.
 * @returns {boolean} True if the value is a valid color, false otherwise.
 */
export function isValidColor(value) {
  // Check if it's a valid hex color
  if (typeof value === 'string' && value.startsWith('#')) {
    return isValidHex(value)
  }

  // Check if it's a valid RGB number
  return isValidRgbNumber(value)
}

/**
 * Checks if the eye dropper is supported in the current browser.
 *
 * @returns {boolean}
 */
export function isEyeDropperSupported() {
  return 'EyeDropper' in window
}

/**
 * Handles the eye dropper functionality.
 *
 * @return {Promise<{h: number, s: number, b: number}>} A promise that resolves when the eye dropper functionality is handled.
 */
export async function handleEyeDropper() {
  if (!('EyeDropper' in window)) {
    alert(
      'Eye dropper is not supported in this browser. Please use Chrome 95+ or Edge 95+.'
    )
    return null
  }

  try {
    const eyeDropper = new EyeDropper()

    const result = await eyeDropper.open()

    if (result && result.sRGBHex) {
      // Convert hex to HSB
      return hexToHsb(result.sRGBHex)
    }
  } catch (error) {
    if (error.name !== 'AbortError') {
      alert('Error using eye dropper: ' + error.message)
    }
    return null
  }
}

/**
 * Adjusts the shade of an RGB color.
 *
 * @param {Object} rgb - The RGB color object.
 * @param {number} shade - The shade value.
 * @return {Object} The adjusted RGB color object.
 */
export function adjustShade(rgb, shade) {
  const shadeString = shade > 0 ? '+' + shade : shade
  const rgbString = this.call(
    'getRgbTone',
    [rgb.r, rgb.g, rgb.b, 1],
    shadeString
  )
  const newRgb = rgbString.split(', ')
  return {
    r: newRgb[0],
    g: newRgb[1],
    b: newRgb[2]
  }
}

/**
 * Deep compares two objects and returns true if they are equal, false otherwise
 * @param {Object} obj1 - The first object to compare
 * @param {Object} obj2 - The second object to compare
 * @returns {boolean} True if the objects are equal, false otherwise
 */
export function deepCompareObjects(obj1, obj2) {
  if (obj1 === obj2) {
    return true
  }

  if (
    typeof obj1 !== 'object' ||
    obj1 === null ||
    typeof obj2 !== 'object' ||
    obj2 === null
  ) {
    return false
  }

  const obj1Keys = Object.keys(obj1)
  const obj2Keys = Object.keys(obj2)

  if (obj1Keys.length !== obj2Keys.length) {
    return false
  }

  for (const key of obj1Keys) {
    if (
      !obj2.hasOwnProperty(key) ||
      !deepCompareObjects(obj1[key], obj2[key])
    ) {
      return false
    }
  }

  return true
}
